/**
 * ProgressMatrix Component
 *
 * Displays the 336-cell progress matrix showing mastery across all domains and tiers.
 * Visual grid representation of user's learning journey.
 */

import React, { useState, useEffect } from 'react';
import { Lock, LockOpen, RefreshCw, Star, X } from 'lucide-react';
import selectiveApi from '../../../services/selectiveApi';
import './ProgressMatrix.css';

const ProgressMatrix = ({ userId, yearLevel = 6 }) => {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  useEffect(() => {
    fetchMatrix();
  }, [yearLevel]);

  const fetchMatrix = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await selectiveApi.progress.getMatrix(yearLevel);
      setMatrix(data);
    } catch (err) {
      console.error('Failed to fetch progress matrix:', err);
      setError('Failed to load progress data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (mastery) => {
    if (mastery >= 75) return '#10b981'; // green - mastered
    if (mastery >= 60) return '#f59e0b'; // orange - proficient
    if (mastery >= 40) return '#3b82f6'; // blue - developing
    if (mastery > 0) return '#94a3b8'; // gray - foundation
    return '#e2e8f0'; // light gray - locked
  };

  const getTierStatus = (tier) => {
    if (tier.status === 'mastered') return 'Mastered';
    if (tier.status === 'in_progress') return 'In Progress';
    if (tier.status === 'unlocked') return 'Unlocked';
    return 'Locked';
  };

  const getTierIcon = (tier) => {
    if (tier.status === 'mastered') return <Star size={14} className="inline-icon" />;
    if (tier.status === 'in_progress') return <RefreshCw size={14} className="inline-icon" />;
    if (tier.status === 'unlocked') return <LockOpen size={14} className="inline-icon" />;
    return <Lock size={14} className="inline-icon" />;
  };

  const handleSkillClick = (section, skill) => {
    setSelectedSkill({ section, skill });
  };

  const closeModal = () => {
    setSelectedSkill(null);
  };

  if (loading) {
    return (
      <div className="progress-matrix-loading">
        <div className="spinner"></div>
        <p>Loading your progress matrix...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="progress-matrix-error">
        <p className="error-message">{error}</p>
        <button onClick={fetchMatrix} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  if (!matrix) {
    return null;
  }

  return (
    <div className="progress-matrix">
      {/* Header */}
      <div className="matrix-header">
        <div className="header-info">
          <h2>Your Learning Progress</h2>
          <p className="subtitle">
            Track your mastery across {matrix.sections?.length || 0} sections
          </p>
        </div>

        <div className="header-stats">
          <div className="stat-box">
            <div className="stat-value">{matrix.overall_mastery_percentage}%</div>
            <div className="stat-label">Overall Mastery</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">{matrix.total_skills_mastered}</div>
            <div className="stat-label">Skills Mastered</div>
          </div>
          <div className="stat-box">
            <div className="stat-value">{matrix.total_skills_unlocked}</div>
            <div className="stat-label">Skills Unlocked</div>
          </div>
        </div>

        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            Grid View
          </button>
          <button
            className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            List View
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="matrix-legend">
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#10b981' }}></span>
          <span>Mastered (75%+)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#f59e0b' }}></span>
          <span>Proficient (60-74%)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#3b82f6' }}></span>
          <span>Developing (40-59%)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#94a3b8' }}></span>
          <span>Foundation (1-39%)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#e2e8f0' }}></span>
          <span>Locked</span>
        </div>
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="matrix-grid-view">
          {matrix.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="section-block">
              <div className="section-header">
                <h3>{section.section_name}</h3>
                <span className="section-count">{section.skills?.length || 0} skills</span>
              </div>

              <div className="skills-grid">
                {section.skills.map((skill, skillIndex) => (
                  <div
                    key={skillIndex}
                    className="skill-card"
                    onClick={() => handleSkillClick(section, skill)}
                  >
                    <div className="skill-name">{skill.sub_skill_name}</div>

                    <div className="tier-row">
                      {skill.tiers.map((tier, tierIndex) => (
                        <div
                          key={tierIndex}
                          className="tier-cell"
                          style={{ backgroundColor: getTierColor(tier.mastery_percentage) }}
                          title={`${tier.tier}: ${tier.mastery_percentage}%`}
                        >
                          <span className="tier-icon">{getTierIcon(tier)}</span>
                        </div>
                      ))}
                    </div>

                    <div className="skill-mastery">
                      {skill.overall_mastery_percentage}% mastered
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="matrix-list-view">
          {matrix.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="section-list">
              <div className="section-list-header">
                <h3>{section.section_name}</h3>
              </div>

              <div className="skills-list">
                {section.skills.map((skill, skillIndex) => (
                  <div key={skillIndex} className="skill-list-item">
                    <div className="skill-list-info">
                      <h4>{skill.sub_skill_name}</h4>
                      <div className="skill-list-stats">
                        <span>{skill.total_questions_attempted} questions</span>
                        <span>{skill.overall_mastery_percentage}% mastery</span>
                      </div>
                    </div>

                    <div className="tier-list">
                      {skill.tiers.map((tier, tierIndex) => (
                        <div
                          key={tierIndex}
                          className="tier-list-item"
                          style={{ borderLeftColor: getTierColor(tier.mastery_percentage) }}
                        >
                          <div className="tier-list-header">
                            <span className="tier-name">{tier.tier}</span>
                            <span className="tier-status">{getTierStatus(tier)}</span>
                          </div>
                          <div className="tier-list-progress">
                            <div className="progress-bar-container">
                              <div
                                className="progress-bar-fill"
                                style={{
                                  width: `${tier.mastery_percentage}%`,
                                  backgroundColor: getTierColor(tier.mastery_percentage)
                                }}
                              />
                            </div>
                            <span className="tier-list-percentage">
                              {tier.mastery_percentage}%
                            </span>
                          </div>
                          <div className="tier-list-meta">
                            {tier.questions_attempted > 0 ? (
                              <>
                                <span>{tier.questions_correct}/{tier.questions_attempted} correct</span>
                                {tier.last_practiced_at && (
                                  <span>Last: {new Date(tier.last_practiced_at).toLocaleDateString()}</span>
                                )}
                              </>
                            ) : (
                              <span className="no-practice">No practice yet</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      className="practice-skill-btn"
                      onClick={() => handleSkillClick(section, skill)}
                    >
                      View Details
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedSkill && (
        <div className="skill-modal-overlay" onClick={closeModal}>
          <div className="skill-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}><X size={18} /></button>

            <div className="modal-header">
              <h3>{selectedSkill.skill.sub_skill_name}</h3>
              <p className="modal-section">{selectedSkill.section.section_name}</p>
            </div>

            <div className="modal-stats">
              <div className="modal-stat">
                <span className="modal-stat-label">Overall Mastery</span>
                <span className="modal-stat-value">
                  {selectedSkill.skill.overall_mastery_percentage}%
                </span>
              </div>
              <div className="modal-stat">
                <span className="modal-stat-label">Questions Attempted</span>
                <span className="modal-stat-value">
                  {selectedSkill.skill.total_questions_attempted}
                </span>
              </div>
              <div className="modal-stat">
                <span className="modal-stat-label">Correct Answers</span>
                <span className="modal-stat-value">
                  {selectedSkill.skill.total_questions_correct}
                </span>
              </div>
            </div>

            <div className="modal-tiers">
              <h4>Tier Progress</h4>
              {selectedSkill.skill.tiers.map((tier, index) => (
                <div key={index} className="modal-tier-card">
                  <div className="modal-tier-header">
                    <span className="modal-tier-icon">{getTierIcon(tier)}</span>
                    <span className="modal-tier-name">{tier.tier}</span>
                    <span className="modal-tier-status">{getTierStatus(tier)}</span>
                  </div>

                  <div className="modal-tier-progress">
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{
                          width: `${tier.mastery_percentage}%`,
                          backgroundColor: getTierColor(tier.mastery_percentage)
                        }}
                      />
                    </div>
                    <span className="modal-tier-percentage">{tier.mastery_percentage}%</span>
                  </div>

                  {tier.questions_attempted > 0 ? (
                    <div className="modal-tier-details">
                      <p>
                        <strong>Questions:</strong> {tier.questions_correct}/{tier.questions_attempted} correct
                      </p>
                      {tier.last_practiced_at && (
                        <p>
                          <strong>Last Practiced:</strong>{' '}
                          {new Date(tier.last_practiced_at).toLocaleDateString('en-AU', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="modal-tier-locked">
                      {tier.unlock_requirements || 'Start practicing to unlock'}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="modal-actions">
              <button className="modal-action-btn primary">
                Start Practice Session
              </button>
              <button className="modal-action-btn secondary" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressMatrix;
